import { apiClient } from 'services/api';
import type { Contact, User } from 'services/entities/PersonEntity';
import { ContactFactory, UserFactory } from 'services/repositories/factories/PersonFactory';
import { Endpoints } from 'utils/endpoints';
import type { Proposal, Company, ShareTemplate } from '../../entities/Proposal';
import { ProposalStatus } from '../../entities/ProposalStatus';
import type { ProposalRepository } from '../ProposalRepository';
import { documentServiceClient } from 'services/api/client';
import type { LegacyCompanyId, LegacyPersonId, MigrationCompanyId, MigrationPersonId } from 'services/entities/MigrationEntities';

export type GetProposalByIdApiResponse = {
  id: number;
  name: string;
  client_name: string;
  status_enum: string;
  person_id: LegacyPersonId;
  document_lead: {
    name: string;
    user_id: string;
  };
  language_code: string;
  client_company_id?: string;
};

export type GetAccountUsersApiResponse = {
  id: number | string;
  email: string;
  first_name: string;
  last_name: string;
  is_primary?: number;
};

export type GetPersonApiResponse = {
  company_id?: LegacyCompanyId;
  id: number | string;
  email: string;
  first_name: string;
  last_name: string;
};

export type GetCompanyApiResponse = {
  people: GetAccountUsersApiResponse[];
};

export type GetShareTemplateApiResponse = {
  final_message: string;
  final_subject: string;
};

class ApiProposalRepositoryClass implements ProposalRepository {
  public async getProposalById(proposalId: number): Promise<Proposal> {
    const { id, name, client_name, status_enum, person_id, document_lead, language_code, client_company_id } =
      await apiClient.get<GetProposalByIdApiResponse>(Endpoints.getProposalById(proposalId));

    const isSent = status_enum !== ProposalStatus.Unsent && status_enum !== ProposalStatus.Awaiting_Approval;

    return {
      id,
      name,
      clientName: client_name,
      isSent,
      personId: person_id,
      documentLead: { name: document_lead.name, userId: document_lead.user_id },
      languageCode: language_code,
      clientCompanyId: client_company_id,
    };
  }

  public async getAccountUsers(): Promise<User[]> {
    const data = await apiClient.get<GetAccountUsersApiResponse[]>(Endpoints.getLegacyUser);

    return data.map((resp) => UserFactory(resp));
  }

  public async getPersonById(personId: MigrationPersonId): Promise<Contact> {
    const resp = await apiClient.get<GetPersonApiResponse>(Endpoints.getPersonById(personId));

    return ContactFactory(resp);
  }

  public async getCompanyById(companyId: MigrationCompanyId): Promise<Company> {
    const { people } = await documentServiceClient.get<GetCompanyApiResponse>(Endpoints.getCompaniesById(companyId));

    return {
      people: people.map((contact) => ContactFactory(contact)),
    };
  }

  public async getCompanyByIdForV2(companyId: MigrationCompanyId): Promise<Company> {
    const { people } = await apiClient.get<GetCompanyApiResponse>(Endpoints.getCompanyById(companyId));

    return {
      people: people.map((contact) => ContactFactory(contact)),
    };
  }

  public async getDefaultShareTemplate(proposalId: number): Promise<ShareTemplate> {
    const { final_message, final_subject } = await apiClient.get<GetShareTemplateApiResponse>(
      Endpoints.getShareProposalDefaultTemplate(proposalId)
    );

    return {
      finalMessage: final_message,
      finalSubject: final_subject,
    };
  }

  public async getProposalShareTemplate(payload: { proposalId: number; templateId: string }): Promise<ShareTemplate> {
    const { final_message, final_subject } = await apiClient.get<GetShareTemplateApiResponse>(Endpoints.getShareProposalTemplate(payload));

    return {
      finalMessage: final_message,
      finalSubject: final_subject,
    };
  }
}

export const ApiProposalRepository = new ApiProposalRepositoryClass();
